import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { FieldPosition, GpsGapKind } from '@groupe-reca/contracts'

// Stockage local de la tablette : UNE base par compte (aucun mélange sur une tablette partagée).

/** Au-delà, les plus anciens points sont écartés (≈ 7 jours de suivi continu à 30 s). */
export const maxQueuedPositions = 20_000

export interface QueuedPosition extends FieldPosition {
  missionId: string
}

export interface QueuedGap {
  id: string
  missionId: string
  kind: GpsGapKind
  startedAt: string
  endedAt: string | null
}

interface OperatorDb extends DBSchema {
  positions: { key: string; value: QueuedPosition; indexes: { byCaptured: string } }
  gaps: { key: string; value: QueuedGap }
  keys: { key: string; value: { scope: string; key: string } }
  meta: { key: string; value: { name: string; value: number } }
}

export interface LocalStore {
  enqueuePosition: (position: QueuedPosition) => Promise<void>
  /** Les plus anciens d'abord. */
  pendingPositions: (limit: number) => Promise<QueuedPosition[]>
  countPositions: () => Promise<number>
  removePositions: (ids: string[]) => Promise<void>
  /** Points écartés faute de place depuis l'ouverture de la base. */
  droppedPositions: () => Promise<number>
  saveGap: (gap: QueuedGap) => Promise<void>
  pendingGaps: () => Promise<QueuedGap[]>
  removeGap: (id: string) => Promise<void>
  /** Clé d'idempotence persistée (rechargement de page, résultat incertain). */
  getKey: (scope: string) => Promise<string | undefined>
  setKey: (scope: string, key: string) => Promise<void>
  clearKey: (scope: string) => Promise<void>
}

export function localDatabaseName(userId: string): string {
  return `reca-operator-v3:${userId}`
}

export function openLocalStore(userId: string): LocalStore {
  let database: Promise<IDBPDatabase<OperatorDb>> | null = null
  const db = () =>
    (database ??= openDB<OperatorDb>(localDatabaseName(userId), 1, {
      upgrade(upgrade) {
        upgrade.createObjectStore('positions', { keyPath: 'id' }).createIndex('byCaptured', 'capturedAt')
        upgrade.createObjectStore('gaps', { keyPath: 'id' })
        upgrade.createObjectStore('keys', { keyPath: 'scope' })
        upgrade.createObjectStore('meta', { keyPath: 'name' })
      },
    }))

  return {
    async enqueuePosition(position) {
      const handle = await db()
      const tx = handle.transaction(['positions', 'meta'], 'readwrite')
      await tx.objectStore('positions').put(position)
      const count = await tx.objectStore('positions').count()
      if (count > maxQueuedPositions) {
        let excess = count - maxQueuedPositions
        let cursor = await tx.objectStore('positions').index('byCaptured').openCursor()
        while (cursor && excess > 0) {
          await cursor.delete()
          excess -= 1
          cursor = await cursor.continue()
        }
        const meta = (await tx.objectStore('meta').get('dropped'))?.value ?? 0
        await tx.objectStore('meta').put({ name: 'dropped', value: meta + count - maxQueuedPositions })
      }
      await tx.done
    },
    async pendingPositions(limit) {
      const handle = await db()
      const result: QueuedPosition[] = []
      let cursor = await handle.transaction('positions').store.index('byCaptured').openCursor()
      while (cursor && result.length < limit) {
        result.push(cursor.value)
        cursor = await cursor.continue()
      }
      return result
    },
    async countPositions() {
      return (await db()).count('positions')
    },
    async removePositions(ids) {
      const tx = (await db()).transaction('positions', 'readwrite')
      await Promise.all(ids.map((id) => tx.store.delete(id)))
      await tx.done
    },
    async droppedPositions() {
      return (await (await db()).get('meta', 'dropped'))?.value ?? 0
    },
    async saveGap(gap) {
      await (await db()).put('gaps', gap)
    },
    async pendingGaps() {
      return (await db()).getAll('gaps')
    },
    async removeGap(id) {
      await (await db()).delete('gaps', id)
    },
    async getKey(scope) {
      return (await (await db()).get('keys', scope))?.key
    },
    async setKey(scope, key) {
      await (await db()).put('keys', { scope, key })
    },
    async clearKey(scope) {
      await (await db()).delete('keys', scope)
    },
  }
}

/** Demande au navigateur de ne pas évincer les données locales; refus possible (non garanti). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    return await navigator.storage.persist()
  } catch {
    return false
  }
}
